import React from "react";
import { X } from "lucide-react";
import { type AddAddressModalProps } from "./types";

const AddAddressModal: React.FC<AddAddressModalProps> = ({
  show,
  newAddress,
  isLoading,
  editingAddressId,
  setNewAddress,
  onClose,
  onAddressChange,
  onSubmit,
}) => {
  if (!show) return null;

  const isEditMode = editingAddressId !== null && editingAddressId !== undefined;
  const title = isEditMode ? "Edit address" : "Add new address";
  const buttonText = isEditMode ? "Update Address" : "Add Address";

  const [error, setError] = React.useState<string | null>(null);

  const validateFields = () => {
    if (
      !newAddress.address_type_value ||
      !newAddress.street ||
      !newAddress.city ||
      !newAddress.building ||
      !newAddress.floor ||
      !newAddress.apartment
    ) {
      setError("Please fill all required fields.");
      return false;
    }
    setError(null);
    return true;
  };

  const handleSubmit = () => {
    if (validateFields()) onSubmit();
  };

  const inputClass =
    "w-full px-3 py-2 bg-section-surface border border-section-border rounded-md text-section-text placeholder-section-muted focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold/40 transition-colors";

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-section-card-bg rounded-xl max-w-lg w-full max-h-[95vh] overflow-y-auto border border-section-border shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-section-border">
          <h2 className="text-lg font-semibold text-section-text">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-section-surface rounded-full transition-colors">
            <X className="w-5 h-5 text-section-muted" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Address Type */}
          <div>
            <label className="block text-sm font-medium text-section-text mb-2">
              Address Type <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Address type (e.g., Home, Work)"
              value={newAddress.address_type_value || ""}
              onChange={(e) => setNewAddress({ ...newAddress, address_type_value: e.target.value })}
              className={inputClass}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-section-text mb-2">
              Street Address <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Street address"
              value={newAddress.street}
              onChange={(e) => onAddressChange("street", e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-section-text mb-2">
              City <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="City"
              value={newAddress.city}
              onChange={(e) => onAddressChange("city", e.target.value)}
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-section-text mb-2">
                Building <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Building"
                value={newAddress.building}
                onChange={(e) => onAddressChange("building", e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-section-text mb-2">
                Floor <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Floor"
                value={newAddress.floor}
                onChange={(e) => onAddressChange("floor", e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-section-text mb-2">
                Apartment <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Apartment"
                value={newAddress.apartment}
                onChange={(e) => onAddressChange("apartment", e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-section-text mb-2">Description</label>
            <textarea
              placeholder="Additional address details"
              value={newAddress.description}
              onChange={(e) => onAddressChange("description", e.target.value)}
              rows={1}
              className={`${inputClass} resize-y overflow-hidden`}
            />
          </div>

          {/* Default checkbox */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={newAddress.is_default}
              onChange={(e) => onAddressChange("is_default", e.target.checked)}
              className="rounded border-section-border bg-section-surface text-primary focus:ring-gold/40"
            />
            <label className="text-sm text-section-text">Set as default shipping address</label>
          </div>

          {error && <div className="text-red-500 text-sm">{error}</div>}

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="flex-1 bg-primary text-primary-foreground py-3 rounded-lg font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {isLoading ? (isEditMode ? "Updating..." : "Adding...") : buttonText}
            </button>
            <button
              onClick={onClose}
              className="flex-1 border border-section-border text-section-muted py-3 rounded-lg font-medium hover:bg-section-surface transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAddressModal;
